// Client-side photo quality check: blur (Laplacian variance) + brightness/glare.
// Non-blocking — returns { ok, warnings: [] } so callers can warn but still upload.
export async function checkImageQuality(file) {
  try {
    const img = await loadImage(file);
    const size = 256;
    const canvas = document.createElement("canvas");
    const scale = Math.min(size / img.width, size / img.height, 1);
    canvas.width = Math.max(32, Math.round(img.width * scale));
    canvas.height = Math.max(32, Math.round(img.height * scale));
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);

    const gray = new Float32Array(width * height);
    let sum = 0, bright = 0, dark = 0;
    for (let i = 0; i < width * height; i++) {
      const g = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
      gray[i] = g;
      sum += g;
      if (g > 245) bright++;
      if (g < 12) dark++;
    }
    const n = width * height;
    const mean = sum / n;

    // Laplacian variance (sharpness)
    let lapSum = 0, lapSqSum = 0, count = 0;
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const i = y * width + x;
        const lap = 4 * gray[i] - gray[i - 1] - gray[i + 1] - gray[i - width] - gray[i + width];
        lapSum += lap;
        lapSqSum += lap * lap;
        count++;
      }
    }
    const lapVar = lapSqSum / count - (lapSum / count) ** 2;

    const warnings = [];
    if (lapVar < 45) warnings.push("This photo looks blurry — hold steady and retake if possible.");
    if (bright / n > 0.18) warnings.push("Strong glare detected — tilt the card away from the light.");
    if (mean < 55) warnings.push("The photo is quite dark — try better lighting.");
    return { ok: warnings.length === 0, warnings, sharpness: Math.round(lapVar) };
  } catch {
    return { ok: true, warnings: [] };
  }
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = reject;
    img.src = url;
  });
}
